import { diToFormData, hookFormToVaultData, type VaultFormData, vaultDataToHookForm } from '@onefootprint/core';
import type { InvestorProfileDeclaration, VaultData } from '@onefootprint/request-types';
import defaults from 'lodash/defaults';
import isEmpty from 'lodash/isEmpty';
import type { InvestorProfileFormData } from '../types';
import { arrayToCommaSeparated, convertCommaSeparatedFieldToArray } from './array-string-conversion';

const INVESTOR_PROFILE_DEFAULTS: VaultData = {
  'investor_profile.employment_status': 'employed',
  'investor_profile.occupation': '',
  'investor_profile.employer': '',
  'investor_profile.annual_income': 'le25k',
  'investor_profile.net_worth': 'le50k',
  'investor_profile.funding_sources': [],
  'investor_profile.investment_goals': [],
  'investor_profile.risk_tolerance': 'conservative',
  'investor_profile.brokerage_firm_employer': '',
  'investor_profile.political_organization': '',
  'investor_profile.senior_executive_symbols': [],
  'investor_profile.family_member_names': [],
};

export const fromVaultData = (vaultData?: VaultData): InvestorProfileFormData => {
  // Apply defaults to raw vault data
  const filteredVaultData = vaultData
    ? Object.fromEntries(Object.entries(vaultData).filter(([, value]) => value != null)) // Because lodash.defaults only fills undefined keys and doesn't replace null values
    : {};
  const vaultDataWithDefaults = defaults({}, filteredVaultData, INVESTOR_PROFILE_DEFAULTS);
  const declarations = vaultDataWithDefaults['investor_profile.declarations'];
  const vaultFormData = vaultDataToHookForm(vaultDataWithDefaults);

  return {
    ...vaultFormData,
    // Convert array fields to comma-separated strings for form input
    [diToFormData('investor_profile.senior_executive_symbols')]: arrayToCommaSeparated(
      vaultDataWithDefaults['investor_profile.senior_executive_symbols'],
    ),
    [diToFormData('investor_profile.family_member_names')]: arrayToCommaSeparated(
      vaultDataWithDefaults['investor_profile.family_member_names'],
    ),
    isAffiliatedWithUsBroker: declarations?.includes('affiliated_with_us_broker') ?? false,
    isSeniorExecutive: declarations?.includes('senior_executive') ?? false,
    isSeniorPoliticalFigure: declarations?.includes('senior_political_figure') ?? false,
    declarationFiles: [],
  } as InvestorProfileFormData;
};

const buildDeclarations = (
  isAffiliatedWithUsBroker: boolean,
  isSeniorExecutive: boolean,
  isSeniorPoliticalFigure: boolean,
): InvestorProfileDeclaration[] => {
  const declarations: InvestorProfileDeclaration[] = [];
  if (isAffiliatedWithUsBroker) declarations.push('affiliated_with_us_broker');
  if (isSeniorExecutive) declarations.push('senior_executive');
  if (isSeniorPoliticalFigure) declarations.push('senior_political_figure');
  return declarations;
};

export const toVaultData = (data: InvestorProfileFormData): VaultData => {
  const {
    isAffiliatedWithUsBroker,
    isSeniorExecutive,
    isSeniorPoliticalFigure,
    declarationFiles: _,
    ...vaultFields
  } = data;

  // Convert form data to vault format
  const rawVaultData = hookFormToVaultData(vaultFields as unknown as VaultFormData);

  // Convert comma-separated strings back to arrays in vault data
  convertCommaSeparatedFieldToArray(rawVaultData, 'investor_profile.senior_executive_symbols');
  convertCommaSeparatedFieldToArray(rawVaultData, 'investor_profile.family_member_names');

  // Filter out empty values
  const vaultData = Object.fromEntries(
    Object.entries(rawVaultData).filter(([, value]) => !isEmpty(value)),
  ) as VaultData;

  // Always include declarations even if empty, otherwise the declarations requirement is considered incomplete
  // Declaration files are processed separately in CollectInvestorProfile
  vaultData['investor_profile.declarations'] = buildDeclarations(
    isAffiliatedWithUsBroker,
    isSeniorExecutive,
    isSeniorPoliticalFigure,
  );

  return vaultData;
};
